import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdModal, CmdModalFooterButton, CmdTextarea } from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import ApiService from '../../Services/ApiService';
import { IFilesImportInput } from '.';
import { PENDING } from '../DynamicTable/TableBody/TableRow';
import usePageWithForm from '../../hooks/usePageWithForm';
import { transformErrors, validateFields } from '../../Services/HookFormService';

const RESPONSE_OK = 'ok';

interface ModalFormProps {
  handleClose: () => void;
  data?: IFilesImportInput[];
  id: number | null;
  customerId?: number | null;
  updateTable: () => void;
  action: string;
  activeRows?: { [key: number]: number[] };
}

type FormValues = {
  note: string;
};

export default function ActionForm(props: ModalFormProps): JSX.Element {
  usePageWithForm();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const { formatMessage: f } = useIntl();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      note: '',
    },
    resolver: async (data) => {
      const formValidation = {
        note: [
          {
            param: 'maxLength',
            value: 250,
            message: f({ id: 'form.numberMax250' }),
          },
        ],
      };
      const validateErros = validateFields(data, formValidation);
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const getStatus = () => {
    if (props.action === PENDING) {
      return 'pending';
    }
    return 'ignored';
  };

  // save
  const handleUpdate = async (updateValues) => {
    const newValues = { ...updateValues, status: getStatus() };
    if (props.activeRows) {
      newValues.expenseImportRecordId = props.activeRows;
    } else {
      newValues.customerId = props.customerId;
    }
    setIsLoaderActive(true);

    const response = await ApiService.put(
      props.activeRows
        ? `/v1/expense/import/record/change-status`
        : `/v1/expense/import/record/${props.id}/change-status`,
      newValues
    );
    setIsLoaderActive(false);
    if (response.data && response.data.status === RESPONSE_OK) {
      props.handleClose();
      props.updateTable();
    }
  };

  const handleFormData = (data: IFilesImportInput[]) => {
    return {
      note: data.find((i) => i.indexId === 'note')?.value,
    };
  };

  React.useEffect(() => {
    if (props.data) {
      setValue('note', (handleFormData(props.data).note || '') as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'modalClose',
      type: 'button',
      title: f({ id: 'form.close' }),
      className: 'e-button',
      closeCallback: props.handleClose,
      tooltip: f({ id: 'alarms.modal.timeFrame.button.close.tooltip' }),
    },
    {
      id: 'submitEditForm',
      type: 'submit',
      title: f({ id: 'form.save' }),
      className: 'e-button e-button--gray',
      submitCallback: handleSubmit(handleUpdate),
    },
  ];

  return (
    <CmdModal
      id="files-import-pending-ignored-form"
      title={
        props.activeRows
          ? `${f({ id: 'tab.expense/import/record' })} (${f({ id: 'table.detail' })})`
          : f({ id: 'table.detail' })
      }
      footerButtons={buttons}
      loader={isLoaderActive}
    >
      <Controller
        name="note"
        control={control}
        render={({ field }) => (
          <CmdTextarea
            {...field}
            name="note"
            id="note"
            label={f({ id: 'form.inputs.note' })}
            rows={5}
            setValue={(value) => {
              setValue('note', value);
              trigger('note');
            }}
            error={errors.note?.message}
            touched={!!errors.note}
          />
        )}
      />
    </CmdModal>
  );
}
